import { useEffect, useState } from 'react'
import Swal from 'sweetalert2'
import IPButton from '../../../../components/IPButton'
import IPInput from '../../../../components/IPInput'
import IPModal from '../../../../components/IPModal'
import { BaseComponentWithContextProps } from '../../../../base/customHooks/componentHOC'
import { IException } from '../../../../base/models'
import { AppleDepOrganizationIdModeOptions, IAppleDepDealerSettings } from '../models/models'
import {
  AdminDeleteDealerSettingsApi,
  AdminGetDealerSettingsApi,
  AdminSaveDealerSettingsApi,
} from '../services/appleDepAdminApi'

const emptySettings = (): IAppleDepDealerSettings => ({
  Id: 0,
  DealerCode: '',
  AgreementAcceptedOn: '',
  OrganizationIdMode: 'EndCustomer',
  OwnOrganizationId: '',
  OwnOrganizationName: '',
  Active: true,
  Note: '',
})

const modeLabel = (value?: string) =>
  AppleDepOrganizationIdModeOptions.find(o => o.value === value)?.label ?? 'Son kullanıcıdan alınır'

const allowsDealerOwn = (mode?: string) => mode === 'DealerOwn' || mode === 'Both'

/**
 * Bayi bazlı Apple DEP ayarları.
 *
 * <b>Reseller ID burada yoktur.</b> Gönderimlerde Index'in tek Reseller ID'si kullanılır
 * ve kaynağı yalnızca `appsettings → AppleDep.ResellerId`. Bayi bazında değişen tek şey
 * cihazın hangi Apple Business Manager hesabına kaydedileceğidir.
 *
 * Bayinin kendi Organization ID'sini normalde <b>bayi kendi ekranından</b> girer;
 * buradaki alan görüntüleme ve gerektiğinde düzeltme içindir.
 */
export default function DealerSettingsTab(props: BaseComponentWithContextProps) {
  const [search, setSearch] = useState('')
  const [settings, setSettings] = useState<IAppleDepDealerSettings[]>([])
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState<IAppleDepDealerSettings>(emptySettings())

  const showError = (err: any, fallback = 'İşlem sırasında bir hata oluştu.') => {
    const exception = err as IException
    Swal.fire({
      icon: 'error',
      title: exception?.description ?? fallback,
      showConfirmButton: true,
      allowOutsideClick: false,
    })
  }

  const loadSettings = async () => {
    props.showLoading()
    try {
      const api: AdminGetDealerSettingsApi = new AdminGetDealerSettingsApi({})
      setSettings(await api.getDealerSettings(search))
    } catch (err: any) {
      showError(err, 'Ayar listesi alınamadı.')
    } finally {
      props.hideLoading()
    }
  }

  useEffect(() => {
    loadSettings()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const save = async () => {
    if (!editing.DealerCode?.trim()) {
      Swal.fire({ icon: 'warning', title: 'Bayi kodu zorunludur.' })
      return
    }

    props.showLoading()
    try {
      const api: AdminSaveDealerSettingsApi = new AdminSaveDealerSettingsApi({})
      await api.saveDealerSettings(editing)
      setShowModal(false)
      await loadSettings()
      Swal.fire({ icon: 'success', title: 'Ayar kaydedildi.', timer: 1500, showConfirmButton: false })
    } catch (err: any) {
      showError(err, 'Ayar kaydedilemedi.')
    } finally {
      props.hideLoading()
    }
  }

  const remove = async (item: IAppleDepDealerSettings) => {
    const confirmation = await Swal.fire({
      icon: 'warning',
      title: 'Ayar silinsin mi?',
      text: `${item.DealerCode} — bayi varsayılan moda (son kullanıcıdan alınır) döner.`,
      showCancelButton: true,
      confirmButtonText: 'Sil',
      cancelButtonText: 'Vazgeç',
    })

    if (!confirmation.isConfirmed) return

    props.showLoading()
    try {
      const api: AdminDeleteDealerSettingsApi = new AdminDeleteDealerSettingsApi({})
      await api.deleteDealerSettings(item.Id)
      await loadSettings()
    } catch (err: any) {
      showError(err, 'Ayar silinemedi.')
    } finally {
      props.hideLoading()
    }
  }

  return (
    <div className='appleDepDealerSettings'>
      <div className='appleDepInfoBox'>
        Gönderimlerde kullanılan <strong>Reseller ID tek bir değerdir</strong> ve
        <code> appsettings → AppleDep.ResellerId </code> ayarından gelir. Bayi bazında bir
        Reseller ID tanımı yoktur. Bu ekran yalnızca cihazın hangi Apple Business Manager
        hesabına kaydedileceğini belirler.
      </div>

      <div className='appleDepFilters'>
        <div className='row'>
          <div className='col-12 col-md-4 mb-2'>
            <label className='labeltext'>Bayi kodu veya Organization ID</label>
            <IPInput type='text' value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className='col-12 col-md-3 mb-2 appleDepFilterAction'>
            <IPButton text='Listele' className='appleDepPrimaryButton' onClick={loadSettings} />
          </div>
        </div>
      </div>

      <div className='appleDepToolbar'>
        <div className='appleDepToolbarInfo'>
          Ayarı olmayan bayiler varsayılan modda çalışır: Organization ID son kullanıcıdan alınır.
          Bayi cihazları <strong>kendi</strong> ABM hesabına kaydedecekse burada modu değiştirin;
          Organization ID'yi bayi kendi ekranından girer, gerekirse buradan düzeltebilirsiniz.
        </div>
        <IPButton
          text='Yeni Ayar'
          className='appleDepPrimaryButton'
          onClick={() => {
            setEditing(emptySettings())
            setShowModal(true)
          }}
        />
      </div>

      {settings.length === 0 ? (
        <div className='appleDepEmpty'>
          Bayi bazlı ayar yok. Tüm bayiler varsayılan modda: Organization ID son kullanıcıdan alınır.
        </div>
      ) : (
        <div className='appleDepTableWrapper'>
          <table className='table table-hover appleDepTable'>
            <thead>
              <tr>
                <th>Bayi Kodu</th>
                <th>Bayi Adı</th>
                <th>Organization ID Kaynağı</th>
                <th>Bayinin Kendi ABM ID'si</th>
                <th>Agreement Tarihi</th>
                <th>Durum</th>
                <th>Not</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {settings.map(item => (
                <tr key={item.Id}>
                  <td>
                    <strong>{item.DealerCode}</strong>
                  </td>
                  <td>{item.DealerName ?? '-'}</td>
                  <td>{modeLabel(item.OrganizationIdMode)}</td>
                  <td>
                    {item.OwnOrganizationId ? (
                      <>
                        <span className='appleDepMonoCell'>{item.OwnOrganizationId}</span>
                        {item.OwnOrganizationName && (
                          <div className='appleDepHint'>{item.OwnOrganizationName}</div>
                        )}
                      </>
                    ) : allowsDealerOwn(item.OrganizationIdMode) ? (
                      <span className='appleDepBlockReason'>Bayi henüz girmedi</span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    {item.AgreementAcceptedOn ? (
                      item.AgreementAcceptedOn
                    ) : (
                      <span className='appleDepHint'>Girilmemiş</span>
                    )}
                  </td>
                  <td>
                    <span
                      className={
                        item.Active ? 'appleDepBadge appleDepBadgeSuccess' : 'appleDepBadge appleDepBadgeDefault'
                      }
                    >
                      {item.Active ? 'Aktif' : 'Pasif'}
                    </span>
                  </td>
                  <td>{item.Note}</td>
                  <td className='appleDepRowActions'>
                    <button
                      type='button'
                      className='appleDepLinkButton'
                      onClick={() => {
                        setEditing({ ...item })
                        setShowModal(true)
                      }}
                    >
                      Düzenle
                    </button>
                    <button type='button' className='appleDepLinkButtonDanger' onClick={() => remove(item)}>
                      Sil
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <IPModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={save}
        confirmText='Kaydet'
        width={760}
        title={editing.Id > 0 ? 'Bayi Ayarı Düzenle' : 'Yeni Bayi Ayarı'}
      >
        <div className='appleDepForm'>
          <div className='form-row'>
            <div className='form-group col-md-6'>
              <label className='labeltext'>Bayi SAP Kodu *</label>
              <IPInput
                type='text'
                maxLength={10}
                value={editing.DealerCode}
                onChange={e => setEditing({ ...editing, DealerCode: e.target.value })}
              />
            </div>
            <div className='form-group col-md-6'>
              <label className='labeltext'>Facilitation Agreement Tarihi</label>
              <IPInput
                type='date'
                value={editing.AgreementAcceptedOn ?? ''}
                onChange={e => setEditing({ ...editing, AgreementAcceptedOn: e.target.value })}
              />
            </div>
          </div>

          <div className='form-row'>
            <div className='form-group col-md-12'>
              <label className='labeltext'>Organization ID kaynağı *</label>
              <div className='appleDepRadioGroup appleDepRadioGroupColumn'>
                {AppleDepOrganizationIdModeOptions.map(option => (
                  <label key={option.value}>
                    <input
                      type='radio'
                      name='appleDepOrgMode'
                      checked={(editing.OrganizationIdMode ?? 'EndCustomer') === option.value}
                      onChange={() => setEditing({ ...editing, OrganizationIdMode: option.value })}
                    />{' '}
                    <strong>{option.label}</strong>
                    <div className='appleDepHint'>{option.description}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {allowsDealerOwn(editing.OrganizationIdMode) && (
            <div className='form-row'>
              <div className='form-group col-md-6'>
                <label className='labeltext'>Bayinin Kendi Organization ID'si</label>
                <IPInput
                  type='text'
                  maxLength={32}
                  value={editing.OwnOrganizationId ?? ''}
                  onChange={e => setEditing({ ...editing, OwnOrganizationId: e.target.value })}
                />
                <small className='appleDepHint'>
                  Zorunlu değil — bayi kendi ekranından girer. Buradan görüntüleyip düzeltebilirsiniz.
                </small>
              </div>
              <div className='form-group col-md-6'>
                <label className='labeltext'>Kurum Adı</label>
                <IPInput
                  type='text'
                  maxLength={255}
                  value={editing.OwnOrganizationName ?? ''}
                  onChange={e => setEditing({ ...editing, OwnOrganizationName: e.target.value })}
                />
              </div>
            </div>
          )}

          <div className='form-row'>
            <div className='form-group col-md-12'>
              <label className='labeltext'>
                <input
                  type='checkbox'
                  checked={editing.Active}
                  onChange={e => setEditing({ ...editing, Active: e.target.checked })}
                />{' '}
                Aktif
              </label>
            </div>
          </div>

          <div className='form-row'>
            <div className='form-group col-md-12'>
              <label className='labeltext'>Not</label>
              <IPInput
                type='text'
                maxLength={500}
                value={editing.Note ?? ''}
                onChange={e => setEditing({ ...editing, Note: e.target.value })}
              />
            </div>
          </div>

          <div className='appleDepInfoBox'>
            Son kullanıcıların cihazları hesaplarına düşebilmesi için Index'in Reseller ID'sini
            Apple Business Manager'da <strong>yetkili tedarikçi</strong> olarak eklemesi gerekir.
            Eklenmezse Apple gönderimi reddeder.
          </div>
        </div>
      </IPModal>
    </div>
  )
}
